import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  GetAllInventoryPlans,
  GetAllProducts,
  GetAllProductTypes,
} from "../../api/inventory";
import {
  Filters,
  InventoryData,
  ProductTypesEnum,
  PlanTypes,
  Status,
  ProductTypesObj,
} from "../../types/inventoryData";
import { format, parse, parseISO } from "date-fns";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { EditRounded, HistoryRounded } from "@mui/icons-material";
import { HeadCell, headCellsOG } from "./InventoryComponents/Table/TableTypes";

type InventoryContextContextType = {
  rows: InventoryData[];
  setRows: Dispatch<SetStateAction<InventoryData[]>>;
  selectedProductIds: number[];
  setSelectedProductIds: Dispatch<SetStateAction<number[]>>;
  filteredRows: InventoryData[];
  setFilteredRows: Dispatch<SetStateAction<InventoryData[]>>;
  selectedProduct: InventoryData | undefined;
  setSelectedProduct: Dispatch<SetStateAction<InventoryData | undefined>>;
  editingRow: InventoryData | null;
  setEditingRow: Dispatch<SetStateAction<InventoryData | null>>;
  productTypes: ProductTypesObj[];
  setProductTypes: Dispatch<SetStateAction<ProductTypesObj[]>>;
  selectedFilterValues: Filters;
  setSelectedFilterValues: Dispatch<SetStateAction<Filters>>;
  addBatchNumberAndActions: (products: InventoryData) => InventoryData;
  updateManyRows: (data: InventoryData[]) => void;
  updateSingleRow: (data: InventoryData) => void;
  headCells: HeadCell[];
  setHeadCells: Dispatch<SetStateAction<HeadCell[]>>;
  plans: PlanTypes[];
  setPlans: Dispatch<SetStateAction<PlanTypes[]>>;
  statusCounts: Record<Status, Record<ProductTypesEnum, number>>;
  setStatusCounts: Dispatch<
    SetStateAction<Record<Status, Record<ProductTypesEnum, number>>>
  >;
  fetchPlans: () => Promise<void>;
  isPhoneSize: boolean;
};

const InventoryContext = createContext<InventoryContextContextType | undefined>(
  undefined
);

export const useInventory = () => {
  const ctx = useContext(InventoryContext);
  if (!ctx)
    throw new Error("useInventory must be used withint InventoryProvider");
  return ctx;
};

const InventoryProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [headCells, setHeadCells] = useState<HeadCell[]>(headCellsOG);
  const [rows, setRows] = useState<InventoryData[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<
    InventoryData | undefined
  >();
  const [filteredRows, setFilteredRows] = useState<InventoryData[]>([]);
  const [editingRow, setEditingRow] = useState<InventoryData | null>(null);
  const [productTypes, setProductTypes] = useState<ProductTypesObj[]>([]);
  const [plans, setPlans] = useState<PlanTypes[]>([]);

  useEffect(() => {
    const foundVisibleColumns = localStorage.getItem("visibleColumns");
    if (foundVisibleColumns === null) {
      setHeadCells((prev) => prev.map((cell) => ({ ...cell, visible: true })));
      localStorage.setItem("visibleColumns", JSON.stringify(headCells));
    } else if (foundVisibleColumns !== null) {
      const foundVisColJson: HeadCell[] = JSON.parse(foundVisibleColumns);

      setHeadCells((prev) =>
        prev.map((cell) => {
          const match = foundVisColJson.find((col) => col.id === cell.id);
          return match ? { ...cell, visible: match.visible } : cell;
        })
      );
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("visibleColumns", JSON.stringify(headCells));
  }, [headCells]);

  const [selectedFilterValues, setSelectedFilterValues] = useState<Filters>({
    productType: [],
    size: [],
    side: [],
    status: [],
    location: [],
    note: [],
    plan: [],
    serial: [],
    batchnumber: [],
  });

  useEffect(() => {
    fetchProducts();
    fetchPlans();
    fetchProductTypes();
  }, []);

  const updateManyRows = (data: InventoryData[]) => {
    for (const row of data) {
      updateSingleRow(row);
    }
  };

  const updateSingleRow = (data: InventoryData) => {
    const newRow = addBatchNumberAndActions(data);
    setRows((prev) => {
      return prev.map((oldRow) =>
        oldRow.productId === newRow.productId ? newRow : oldRow
      );
    });
    setFilteredRows((prev) => {
      return prev.map((oldRow) =>
        oldRow.productId === newRow.productId ? newRow : oldRow
      );
    });
  };

  const addBatchNumberAndActions = (row: InventoryData) => {
    const batchnumberValue = row.serial.slice(4, 8) || "";
    const parsedDate = parse(batchnumberValue, "yyMM", new Date());
    const newDate = format(parsedDate, "MMMM yyyy");

    return {
      ...row,
      batchnumber: `${batchnumberValue} (${newDate})`,
      actions: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={(event) => setEditingRow(row)}>
            <EditRounded />
          </IconButton>
          <IconButton onClick={() => setSelectedProduct(row)}>
            <HistoryRounded />
          </IconButton>
        </Box>
      ),
    };
  };

  const fetchProducts = async () => {
    let products: InventoryData[] = await GetAllProducts();

    products = products.map((row: InventoryData) => {
      row.createdAt = new Date(row.createdAt);
      row = addBatchNumberAndActions(row);
      return row;
    });

    // console.log(products);
    setRows(products);
    setFilteredRows(products);
    // printRowData(products);
  };

  const fetchPlans = async () => {
    let plans: PlanTypes[] = await GetAllInventoryPlans();
    if (plans) setPlans(plans);
  };

  const fetchProductTypes = async () => {
    const productTypes = await GetAllProductTypes();
    if (productTypes) setProductTypes(productTypes);
  };

  const updatePlans = () => {};

  const removePlans = () => {};

  const addPlans = () => {};

  type ProductTypesRecord = Record<ProductTypesEnum, number>;
  type StatusProductTypes = Record<Status, ProductTypesRecord>;

  const initialProductTypesRecord: ProductTypesRecord = {
    [ProductTypesEnum.BackVest]: 0,
    [ProductTypesEnum.BackHub]: 0,
    [ProductTypesEnum.ElbowHub]: 0,
    [ProductTypesEnum.ElbowSleeve]: 0,
  };

  const initialStatusProductTypes: StatusProductTypes = {
    [Status.Available]: { ...initialProductTypesRecord },
    [Status.InUse]: { ...initialProductTypesRecord },
    [Status.QA]: { ...initialProductTypesRecord },
    [Status.Defect]: { ...initialProductTypesRecord },
  };

  const [statusCounts, setStatusCounts] = useState<StatusProductTypes>(
    initialStatusProductTypes
  );

  useEffect(() => {
    if (rows.length == 0) return;
    const count = rows.reduce<StatusProductTypes>((acc, row) => {
      const statusKey = row.status;
      acc[statusKey][row.productType.name] += 1;
      return acc;
    }, initialStatusProductTypes);

    setStatusCounts(count);
  }, [rows]);

  const theme = useTheme();
  const isPhoneSize = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      <InventoryContext.Provider
        value={{
          rows,
          setRows,
          selectedProductIds,
          setSelectedProductIds,
          selectedProduct,
          setSelectedProduct,
          productTypes,
          setProductTypes,
          filteredRows,
          setFilteredRows,
          editingRow,
          setEditingRow,
          selectedFilterValues,
          setSelectedFilterValues,
          addBatchNumberAndActions,
          updateManyRows,
          updateSingleRow,
          headCells,
          setHeadCells,
          plans,
          setPlans,
          statusCounts,
          setStatusCounts,
          fetchPlans,
          isPhoneSize,
        }}
      >
        {children}
      </InventoryContext.Provider>
    </>
  );
};

export default InventoryProvider;
